import classNames from 'classnames';
import { isNumber } from 'lodash';
import { Scrollbars, SelectInput } from '@meitu/saas-ui';
import { type TextLayer } from '@meitu/saas-editor-core/es/layers/text-layer';
import {
    MAX_FONT_SIZE_FOR_EDGELESS,
    MIN_FONT_SIZE,
    type ITextLayerProps,
} from '@meitu/saas-editor-core/es/version-types';
import IconFont from '@meitu/xdesign-icon-font';
import { useEditor } from '@/hooks/use-editor';
import { useGlobalWidget } from '@/hooks/use-global-widget';
import { useListenActivePickLayerProps } from '@/hooks/use-listen-layer-props';
import { useTextRangeAttrsState } from '@/hooks/use-text-range-attrs-state';
import { getFontSizeList } from './utils';
import './style.less';

type Props = {
    layerId: string;
};

const FONT_SIZE_LIST = getFontSizeList();
const DEFAULT_FONT_SIZE = 6;

export const EdgelessFontSize: React.FC<Props> = ({ layerId }) => {
    const editor = useEditor();
    const popoverWidgetRef = useGlobalWidget(editor, false);
    const textRangeAttrsState = useTextRangeAttrsState(layerId);

    const props = useListenActivePickLayerProps(editor, (layer) => {
        if (!layer) return null;

        const layerData = layer.data as ITextLayerProps;
        return {
            fontSize: layerData.fontSize,
        };
    });

    const selectionFontSize =
        textRangeAttrsState?.type === 'uniform' || textRangeAttrsState?.type === 'mixed'
            ? textRangeAttrsState.attrs.fontSize
            : null;
    const fontSize = selectionFontSize || props?.fontSize || DEFAULT_FONT_SIZE;

    const handleOpenChange = (open: boolean) => {
        popoverWidgetRef.current = open;
    };

    const handleSelectInputChange = (value: number | null, options = { writeHistory: false }) => {
        if (!isNumber(value)) return;

        let finalValue = value;
        if (value < MIN_FONT_SIZE) finalValue = MIN_FONT_SIZE;
        if (value > MAX_FONT_SIZE_FOR_EDGELESS) finalValue = MAX_FONT_SIZE_FOR_EDGELESS;

        editor.getInstance<TextLayer>('TextLayer').setFontSize(layerId, finalValue, options);
    };

    const handleItemClick = (value: number, event: React.MouseEvent) => {
        event.stopPropagation();
        handleSelectInputChange(value, { writeHistory: true });
    };

    const SelectInputContent = (
        <ul className="edgeless-font-size__list">
            <Scrollbars style={{ height: '242px' }}>
                {FONT_SIZE_LIST.map((value, idx) => {
                    return (
                        <li
                            key={idx}
                            className={classNames('edgeless-font-size__item', {
                                ['edgeless-font-size__item--active']: value === fontSize,
                            })}
                            onClick={(event) => handleItemClick(value, event)}
                        >
                            {value === fontSize ? (
                                <IconFont name="CheckBlack" fontSize={12} className="edgeless-font-size__icon-check" />
                            ) : null}
                            {value}
                        </li>
                    );
                })}
            </Scrollbars>
        </ul>
    );

    return (
        <div className="edgeless-font-size">
            <SelectInput
                showStepBtn
                value={fontSize}
                minVal={MIN_FONT_SIZE}
                maxVal={MAX_FONT_SIZE_FOR_EDGELESS}
                trigger="click"
                width={64}
                height={34}
                decimalPoint={0}
                onOpenChange={handleOpenChange}
                onChange={(value: number | null) => handleSelectInputChange(value, { writeHistory: false })}
                onAfterChange={(value: number | null) => handleSelectInputChange(value, { writeHistory: true })}
                inputContent={SelectInputContent}
            />
        </div>
    );
};
